/**
 * Lightweight in-memory accumulator that tracks action counts and console log
 * counts during an active recording session. The accumulated summary is sent
 * to the server when the session ends (PATCH), so the list view can display
 * it without downloading/parsing all chunks.
 */
import type {SessionReplaySummary, SessionReplaySummaryAction} from './types';

const ENTITY_LABELS: Record<string, string> = {
	matter: 'Cases',
	contact: 'Contacts',
	task: 'Tasks',
	lead: 'Leads',
	document: 'Documents',
	invoice: 'Invoices',
	transaction: 'Payments',
	event: 'Events',
	expense: 'Expenses',
	comment: 'Comments',
};

type OpKey = 'created' | 'edited' | 'deleted' | 'restored';
const OP_DECODE: Record<string, OpKey> = {C: 'created', U: 'edited', D: 'deleted', R: 'restored'};

interface Counts {
	label: string;
	created: number;
	edited: number;
	deleted: number;
	restored: number;
}

let actionCounts = new Map<string, Counts>();
let errorCount = 0;
let warningCount = 0;
let logCount = 0;

export function resetAccumulator(): void {
	actionCounts = new Map();
	errorCount = 0;
	warningCount = 0;
	logCount = 0;
}

/** Called by actionTrackingMiddleware after emitting the custom event */
export function accumulateAction(entityType: string, operation: string): void {
	let counts = actionCounts.get(entityType);
	if (!counts) {
		counts = {
			label: ENTITY_LABELS[entityType] || entityType,
			created: 0, edited: 0, deleted: 0, restored: 0,
		};
		actionCounts.set(entityType, counts);
	}
	const op = OP_DECODE[operation];
	if (op) counts[op]++;
}

/** Called by consoleRecordPlugin after emitting the custom event */
export function accumulateConsole(level: string): void {
	logCount++;
	if (level === 'E') errorCount++;
	else if (level === 'W') warningCount++;
}

/** Build the summary object to send to the server */
export function buildAccumulatedSummary(): SessionReplaySummary | null {
	if (actionCounts.size === 0 && logCount === 0) return null;

	const orderKeys = ['matter', 'contact', 'task', 'lead', 'document', 'invoice', 'transaction', 'event', 'expense', 'comment'];
	const actions: SessionReplaySummaryAction[] = [];

	for (const key of orderKeys) {
		const c = actionCounts.get(key);
		if (c && (c.created + c.edited + c.deleted + c.restored) > 0) {
			actions.push({label: c.label, created: c.created, edited: c.edited, deleted: c.deleted, restored: c.restored});
		}
	}
	// Any remaining entity types not in the standard order
	for (const [key, c] of actionCounts) {
		if (!orderKeys.includes(key) && (c.created + c.edited + c.deleted + c.restored) > 0) {
			actions.push({label: c.label, created: c.created, edited: c.edited, deleted: c.deleted, restored: c.restored});
		}
	}

	// Build text summary
	const parts: string[] = [];
	for (const a of actions) {
		const ops: string[] = [];
		if (a.created > 0) ops.push(`created ${a.created}`);
		if (a.edited > 0) ops.push(`edited ${a.edited}`);
		if (a.deleted > 0) ops.push(`deleted ${a.deleted}`);
		if (a.restored > 0) ops.push(`restored ${a.restored}`);
		if (ops.length > 0) parts.push(`${ops.join(', ')} ${a.label.toLowerCase()}`);
	}
	const text = parts.length > 0 ? parts.join(', ') : 'No tracked actions';

	return {text, actions, errorCount, warningCount, logCount};
}
